import ReactECharts from 'echarts-for-react';

/**
 * AutonomyLadderChart_3 — "dark command-deck" variant of AutonomyLadderChart.
 *
 * Same data and chart family (grouped horizontal bar) as the original.
 * Opaque slate-900 background, light text, teal/cyan accents, subtle
 * gridlines. Confident dashboard feel.
 */

const DECK = {
  teal: '#0AB3BF', // autonomy bars
  cyan: '#4DD4E0', // review bars
  primary: '#F8FAFC', // primary text
  secondary: '#94A3B8', // secondary text
  grid: '#1E293B', // subtle gridlines
  surface: '#0F172A', // opaque dark card
};

const MODES = ['MANUAL', 'SEMI_AUTO', 'FULL_AUTO'];
const AUTONOMY = [0, 60, 90]; // what the bot acts on its own
const REVIEW = [100, 40, 10]; // what a human still gates

/** ONE shared percentage formatter — tooltip, labels, and axis all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

export const AutonomyLadderChart_3 = ({ height = 280 }: { height?: number }) => {
  const option = {
    backgroundColor: DECK.surface,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(148, 163, 184, 0.08)' } },
      backgroundColor: DECK.grid,
      borderColor: DECK.secondary,
      borderWidth: 1,
      textStyle: { color: DECK.primary, fontSize: 13 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const i = params[0].dataIndex;
        return `${MODES[i]}<br/>${params
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value)}</b>`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 8,
      textStyle: { color: DECK.secondary, fontSize: 13 },
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 92, right: 48, top: 48, bottom: 36 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: DECK.secondary, fontSize: 11 },
      axisLabel: { color: DECK.secondary, fontSize: 12, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: DECK.grid, width: 1 } },
    },
    yAxis: {
      type: 'category',
      data: MODES,
      axisLine: { lineStyle: { color: DECK.grid } },
      axisLabel: { color: DECK.primary, fontSize: 13, fontWeight: 700 },
      axisTick: { show: false },
    },
    series: [
      {
        name: 'Autonomy (bot acts)',
        type: 'bar',
        barWidth: '30%',
        data: AUTONOMY.map((v) => ({ value: v, itemStyle: { color: DECK.teal, borderRadius: [0, 6, 6, 0] } })),
        label: {
          show: true,
          position: 'right',
          color: DECK.cyan,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
      {
        name: 'Human review (gates)',
        type: 'bar',
        barWidth: '30%',
        data: REVIEW.map((v) => ({ value: v, itemStyle: { color: DECK.cyan, borderRadius: [0, 6, 6, 0], opacity: 0.55 } })),
        label: {
          show: true,
          position: 'right',
          color: DECK.secondary,
          fontWeight: 700,
          fontSize: 12,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-400 text-center">
        Figure 1. The autonomy ladder: from MANUAL (every action gated) through SEMI_AUTO to FULL_AUTO (audit anytime).
        Autonomy rises with earned trust; human review never disappears. Hover for values.
      </p>
    </div>
  );
};
